import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Download, LogOut, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("fl_admin_token") || "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [leads, setLeads] = useState([]);
  const [quizLeads, setQuizLeads] = useState([]);
  const [auditLeads, setAuditLeads] = useState([]);
  const [bridgeLeads, setBridgeLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("calculator"); // 'calculator' | 'quiz' | 'audit' | 'bridge'

  const login = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      const t = res.data.token;
      setToken(t);
      localStorage.setItem("fl_admin_token", t);
    } catch (err) {
      setError(err?.response?.data?.detail || "Invalid password");
    } finally {
      setSubmitting(false);
    }
  };

  const logout = () => {
    setToken("");
    localStorage.removeItem("fl_admin_token");
    setLeads([]);
    setQuizLeads([]);
    setAuditLeads([]);
    setBridgeLeads([]);
    setStats(null);
  };

  const fetchData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [leadsRes, statsRes, quizRes, auditRes, bridgeRes] = await Promise.all([
        axios.get(`${API}/admin/leads`, { headers }),
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/quiz-leads`, { headers }),
        axios.get(`${API}/admin/audit-leads`, { headers }),
        axios.get(`${API}/admin/bridge-leads`, { headers }),
      ]);
      setLeads(leadsRes.data.leads || []);
      setStats(statsRes.data);
      setQuizLeads(quizRes.data.leads || []);
      setAuditLeads(auditRes.data.leads || []);
      setBridgeLeads(bridgeRes.data.leads || []);
    } catch (err) {
      if (err?.response?.status === 401) logout();
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (!token) {
    return (
      <div className="fl-gate" style={{ gridTemplateColumns: "1fr", justifyItems: "center" }}>
        <form className="fl-gate-card fl-fade-in" onSubmit={login} style={{ maxWidth: 420 }} data-testid="fl-admin-login-form">
          <h3>Admin login</h3>
          <p className="lead-card-sub">Enter your password to view leads.</p>
          <div className="fl-form-group">
            <label htmlFor="admin-pw">Password</label>
            <input
              id="admin-pw"
              type="password"
              className={`fl-input ${error ? "error" : ""}`}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
              data-testid="fl-admin-password"
            />
            {error && <div className="fl-form-error" data-testid="fl-admin-error">{error}</div>}
          </div>
          <button type="submit" className="fl-btn" disabled={submitting} data-testid="fl-admin-login-submit">
            {submitting ? "Logging in…" : "Log in"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="fl-admin" data-testid="fl-admin">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 12 }}>
        <h1 style={{ fontFamily: "'Fraunces', serif", color: "var(--navy)", margin: 0, fontSize: 32 }}>Leads</h1>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button className="fl-btn fl-btn-ghost" onClick={fetchData} data-testid="fl-admin-refresh"><RefreshCw size={14} /> Refresh</button>
          <a
            className="fl-btn fl-btn-ghost"
            href={`${API}/admin/${tab === "quiz" ? "quiz-leads" : tab === "audit" ? "audit-leads" : tab === "bridge" ? "bridge-leads" : "leads"}.csv?token=${encodeURIComponent(token)}`}
            data-testid="fl-admin-download-csv"
          >
            <Download size={14} /> Download CSV
          </a>
          <button className="fl-btn fl-btn-ghost" onClick={logout} data-testid="fl-admin-logout"><LogOut size={14} /> Log out</button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 8, marginBottom: 24, borderBottom: "1px solid rgba(17,47,107,0.1)" }}>
        <button
          onClick={() => setTab("calculator")}
          data-testid="fl-tab-calculator"
          style={{
            background: "transparent", border: "none", padding: "12px 18px",
            fontFamily: "inherit", fontSize: 14, fontWeight: 700, cursor: "pointer",
            color: tab === "calculator" ? "var(--navy)" : "var(--muted)",
            borderBottom: tab === "calculator" ? "3px solid var(--gold)" : "3px solid transparent",
            marginBottom: -1,
          }}
        >Calculator leads ({leads.length})</button>
        <button
          onClick={() => setTab("quiz")}
          data-testid="fl-tab-quiz"
          style={{
            background: "transparent", border: "none", padding: "12px 18px",
            fontFamily: "inherit", fontSize: 14, fontWeight: 700, cursor: "pointer",
            color: tab === "quiz" ? "var(--navy)" : "var(--muted)",
            borderBottom: tab === "quiz" ? "3px solid var(--gold)" : "3px solid transparent",
            marginBottom: -1,
          }}
        >Quiz leads ({quizLeads.length})</button>
        <button
          onClick={() => setTab("audit")}
          data-testid="fl-tab-audit"
          style={{
            background: "transparent", border: "none", padding: "12px 18px",
            fontFamily: "inherit", fontSize: 14, fontWeight: 700, cursor: "pointer",
            color: tab === "audit" ? "var(--navy)" : "var(--muted)",
            borderBottom: tab === "audit" ? "3px solid var(--gold)" : "3px solid transparent",
            marginBottom: -1,
          }}
        >Audit leads ({auditLeads.length})</button>
        <button
          onClick={() => setTab("bridge")}
          data-testid="fl-tab-bridge"
          style={{
            background: "transparent", border: "none", padding: "12px 18px",
            fontFamily: "inherit", fontSize: 14, fontWeight: 700, cursor: "pointer",
            color: tab === "bridge" ? "var(--navy)" : "var(--muted)",
            borderBottom: tab === "bridge" ? "3px solid var(--gold)" : "3px solid transparent",
            marginBottom: -1,
          }}
        >Bridge leads ({bridgeLeads.length})</button>
      </div>

      {tab === "calculator" && stats && (
        <div className="fl-admin-stats">
          <div className="fl-stat"><div className="label">Total leads</div><div className="value">{stats.total}</div></div>
          <div className="fl-stat"><div className="label">Today</div><div className="value">{stats.today}</div></div>
          <div className="fl-stat"><div className="label">Pushed to Kajabi</div><div className="value">{stats.success}</div></div>
          <div className="fl-stat"><div className="label">Failed pushes</div><div className="value">{stats.failed}</div></div>
        </div>
      )}

      {tab === "quiz" && (
        <div className="fl-admin-stats">
          <div className="fl-stat"><div className="label">Total quiz leads</div><div className="value">{quizLeads.length}</div></div>
          <div className="fl-stat">
            <div className="label">Tier 1 (0–10)</div>
            <div className="value">{quizLeads.filter(q => q.tier_id === 1).length}</div>
          </div>
          <div className="fl-stat">
            <div className="label">Tier 2 (11–20)</div>
            <div className="value">{quizLeads.filter(q => q.tier_id === 2).length}</div>
          </div>
          <div className="fl-stat">
            <div className="label">Tier 3 (21–30)</div>
            <div className="value">{quizLeads.filter(q => q.tier_id === 3).length}</div>
          </div>
        </div>
      )}

      {tab === "audit" && (
        <div className="fl-admin-stats">
          <div className="fl-stat"><div className="label">Total audit leads</div><div className="value">{auditLeads.length}</div></div>
          <div className="fl-stat">
            <div className="label">Avg total score</div>
            <div className="value">{auditLeads.length ? Math.round(auditLeads.reduce((a, l) => a + (l.grand_total || 0), 0) / auditLeads.length) : 0}/100</div>
          </div>
          <div className="fl-stat">
            <div className="label">Strong limiting (60+)</div>
            <div className="value">{auditLeads.filter(l => (l.grand_total || 0) >= 60).length}</div>
          </div>
          <div className="fl-stat">
            <div className="label">Healthy (under 30)</div>
            <div className="value">{auditLeads.filter(l => (l.grand_total || 0) < 30).length}</div>
          </div>
        </div>
      )}

      {tab === "bridge" && (
        <div className="fl-admin-stats">
          <div className="fl-stat"><div className="label">Total bridge leads</div><div className="value">{bridgeLeads.length}</div></div>
          <div className="fl-stat">
            <div className="label">Total gratitude days</div>
            <div className="value">{bridgeLeads.reduce((a, l) => a + (l.gratitude_days || 0), 0)}</div>
          </div>
          <div className="fl-stat">
            <div className="label">Total affirmations saved</div>
            <div className="value">{bridgeLeads.reduce((a, l) => a + (l.affirmation_count || 0), 0)}</div>
          </div>
          <div className="fl-stat">
            <div className="label">Active practitioners (≥3 days)</div>
            <div className="value">{bridgeLeads.filter(l => (l.gratitude_days || 0) >= 3).length}</div>
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: "center", padding: 40, color: "var(--muted)" }}>Loading…</div>
      ) : tab === "calculator" ? (
        leads.length === 0 ? (
          <div style={{ textAlign: "center", padding: 60, color: "var(--muted)", background: "#fff", borderRadius: 16, boxShadow: "var(--shadow)" }}>
            No calculator leads yet. Share your calculator link to get started.
          </div>
        ) : (
          <table data-testid="fl-admin-table">
            <thead>
              <tr>
                <th>Date</th><th>Name</th><th>Email</th><th>Phone</th><th>Kajabi</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((l) => (
                <tr key={l.id}>
                  <td>{new Date(l.created_at).toLocaleString()}</td>
                  <td>{l.name}</td>
                  <td>{l.email}</td>
                  <td>{l.phone}</td>
                  <td>
                    <span className={`fl-status-pill ${l.kajabi_status}`}>{l.kajabi_status}</span>
                    {l.kajabi_error && <div style={{ fontSize: 11, color: "#991b1b", marginTop: 4 }}>{l.kajabi_error}</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      ) : tab === "quiz" ? (
        quizLeads.length === 0 ? (
          <div style={{ textAlign: "center", padding: 60, color: "var(--muted)", background: "#fff", borderRadius: 16, boxShadow: "var(--shadow)" }}>
            No quiz leads yet. Share your quiz link (/quiz) to get started.
          </div>
        ) : (
          <table data-testid="fl-admin-quiz-table">
            <thead>
              <tr>
                <th>Date</th><th>Name</th><th>Email</th><th>Score</th><th>Tier</th><th>Focus pillar</th><th>Kajabi</th><th>Email</th>
              </tr>
            </thead>
            <tbody>
              {quizLeads.map((l) => (
                <tr key={l.id}>
                  <td>{new Date(l.created_at).toLocaleString()}</td>
                  <td>{l.name}</td>
                  <td>{l.email}</td>
                  <td><strong style={{ color: "var(--navy)" }}>{l.score}/{l.max_score}</strong></td>
                  <td>{l.tier_name}</td>
                  <td>{l.focus_pillar}</td>
                  <td><span className={`fl-status-pill ${l.kajabi_status}`}>{l.kajabi_status}</span></td>
                  <td><span className={`fl-status-pill ${l.email_status}`}>{l.email_status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      ) : tab === "audit" ? (
        auditLeads.length === 0 ? (
          <div style={{ textAlign: "center", padding: 60, color: "var(--muted)", background: "#fff", borderRadius: 16, boxShadow: "var(--shadow)" }}>
            No audit leads yet. Share your audit link (/audit) to get started.
          </div>
        ) : (
          <table data-testid="fl-admin-audit-table">
            <thead>
              <tr>
                <th>Date</th><th>Name</th><th>Email</th><th>Total</th><th>Primary block</th><th>Kajabi</th><th>Email</th>
              </tr>
            </thead>
            <tbody>
              {auditLeads.map((l) => (
                <tr key={l.id}>
                  <td>{new Date(l.created_at).toLocaleString()}</td>
                  <td>{l.name}</td>
                  <td>{l.email}</td>
                  <td><strong style={{ color: "var(--navy)" }}>{l.grand_total}/100</strong></td>
                  <td>{l.primary_block_title} <span style={{ color: "var(--muted)" }}>({l.primary_block_score}/20)</span></td>
                  <td><span className={`fl-status-pill ${l.kajabi_status}`}>{l.kajabi_status}</span></td>
                  <td><span className={`fl-status-pill ${l.email_status}`}>{l.email_status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      ) : (
        bridgeLeads.length === 0 ? (
          <div style={{ textAlign: "center", padding: 60, color: "var(--muted)", background: "#fff", borderRadius: 16, boxShadow: "var(--shadow)" }}>
            No bridge leads yet. Share your bridge link (/bridge) to get started.
          </div>
        ) : (
          <table data-testid="fl-admin-bridge-table">
            <thead>
              <tr>
                <th>Date</th><th>Name</th><th>Email</th><th>Gratitude days</th><th>Affirmations</th><th>Last login</th><th>Kajabi</th>
              </tr>
            </thead>
            <tbody>
              {bridgeLeads.map((l) => (
                <tr key={l.id || l.email}>
                  <td>{new Date(l.created_at).toLocaleDateString()}</td>
                  <td>{l.name}</td>
                  <td>{l.email}</td>
                  <td><strong style={{ color: "var(--navy)" }}>{l.gratitude_days || 0}</strong></td>
                  <td>{l.affirmation_count || 0}</td>
                  <td style={{ fontSize: 12, color: "var(--muted)" }}>{l.last_login_at ? new Date(l.last_login_at).toLocaleString() : "—"}</td>
                  <td><span className={`fl-status-pill ${l.kajabi_status}`}>{l.kajabi_status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      )}
    </div>
  );
}
